// Hook to fetch the tenant /tenant/setting/key property

/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useCallback, useEffect, useState } from 'react';

interface GetTenantSettingParams {
    includeClients?: boolean;
    platformTenantId?: string;
}

export const useFetchTenantSetting = ({
    clientId,
    key
}: {
    clientId?: string;
    key: string;
}) => {
    const [setting, setSetting] = useState(false);
    const [loading, setLoading] = useState(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => setShouldRefetch({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const params: GetTenantSettingParams = {};
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`/tenantSetting/${key}`, {
                    cancelToken: source.token,
                    params
                });

                if (!Response.hasData(response, { noId: true })) {
                    debug.e(`No Tenant Setting Found for key ${key}`);
                } else {
                    const value = Response.getProperty(response, 'value');
                    setSetting(value);
                }
            } catch (error) {
                debug.e(`Unable to get Tenant Setting for key ${key}`, error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [clientId, key, shouldRefetchData]);

    return {
        loading,
        refetchData,
        setting
    };
};

export const usePostTenantSetting = () => {
    const [saving, setSaving] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const postTenantSetting = useCallback(async (params: any) => {
        try {
            setSaving(true);
            await axios.post('/tenantSetting', params);
            setSaving(false);
            return true; // success = true
        } catch (err) {
            debug.e(`Unable to post tenant setting.`, err);
            setSaving(false);
            return false; // success = false
        }
    }, []);

    return { postTenantSetting, saving };
};
